/**
 * Dashboard Charts Component
 * Displays the compliance distribution charts (By Rules, By Employees, By Category)
 */
import { Card } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

// Chart configuration constants - extracted to prevent re-renders
const CHART_MARGIN_COMPACT = { left: 0, right: 5, top: 0, bottom: 0 };
const AXIS_TICK_SMALL = { fontSize: 9 };
const SQRT_DOMAIN = [0, 'auto'];
const BAR_RADIUS_RIGHT = [0, 2, 2, 0];
const BAR_RADIUS_TOP = [2, 2, 0, 0];

const DashboardCharts = ({ 
  summary, 
  totalEmployees, 
  employeeByStatus, 
  employeeStatusBars,
  categoryData 
}) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 flex-shrink-0">
      {/* By Rules Chart */}
      <Card className="p-3" data-testid="compliance-chart">
        <p className="text-xs font-medium mb-2">By Rules</p>
        <div className="space-y-2">
          <div className="h-6 flex rounded overflow-hidden">
            {summary.total > 0 && (
              <>
                {summary.non_compliant > 0 && (
                  <div 
                    className="bg-red-500 flex items-center justify-center"
                    style={{ width: `${(summary.non_compliant / summary.total) * 100}%` }}
                  >
                    {summary.non_compliant > 10 && <span className="text-[10px] text-white font-medium">{summary.non_compliant}</span>}
                  </div>
                )}
                {summary.over_compliant > 0 && (
                  <div 
                    className="bg-amber-500 flex items-center justify-center"
                    style={{ width: `${(summary.over_compliant / summary.total) * 100}%` }}
                  >
                    {summary.over_compliant > 10 && <span className="text-[10px] text-white font-medium">{summary.over_compliant}</span>}
                  </div>
                )}
                {summary.compliant > 0 && (
                  <div 
                    className="bg-emerald-500 flex items-center justify-center"
                    style={{ width: `${(summary.compliant / summary.total) * 100}%` }}
                  >
                    {summary.compliant > 10 && <span className="text-[10px] text-white font-medium">{summary.compliant}</span>}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px]">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-sm bg-red-500" />
              <span className="text-muted-foreground">Non-Compliant</span>
              <span className="font-medium">{summary.non_compliant}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-sm bg-amber-500" />
              <span className="text-muted-foreground">Over</span>
              <span className="font-medium">{summary.over_compliant}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-sm bg-emerald-500" />
              <span className="text-muted-foreground">Compliant</span>
              <span className="font-medium">{summary.compliant}</span>
            </div>
          </div>
        </div>
      </Card>

      {/* By Employees Chart */}
      <Card className="p-3" data-testid="employee-chart">
        <p className="text-xs font-medium mb-1">By Employees</p>
        <p className="text-[10px] text-muted-foreground mb-2">{totalEmployees.toLocaleString()} total</p>
        <div className="space-y-2">
          <div className="h-6 flex rounded overflow-hidden">
            {totalEmployees > 0 && employeeByStatus.length > 0 && (
              <>
                {employeeStatusBars.nonCompliant && employeeStatusBars.nonCompliant.value > 0 && (
                  <div 
                    key="Non-Compliant"
                    className="bg-red-500 flex items-center justify-center"
                    style={{ width: `${(employeeStatusBars.nonCompliant.value / totalEmployees) * 100}%` }}
                  >
                    {employeeStatusBars.nonCompliant.value > 500 && <span className="text-[10px] text-white font-medium">{employeeStatusBars.nonCompliant.value.toLocaleString()}</span>}
                  </div>
                )}
                {employeeStatusBars.overCompliant && employeeStatusBars.overCompliant.value > 0 && (
                  <div 
                    key="Over-Compliant"
                    className="bg-amber-500 flex items-center justify-center"
                    style={{ width: `${(employeeStatusBars.overCompliant.value / totalEmployees) * 100}%` }}
                  >
                    {employeeStatusBars.overCompliant.value > 500 && <span className="text-[10px] text-white font-medium">{employeeStatusBars.overCompliant.value.toLocaleString()}</span>}
                  </div>
                )}
                {employeeStatusBars.compliant && employeeStatusBars.compliant.value > 0 && (
                  <div 
                    key="Compliant"
                    className="bg-emerald-500 flex items-center justify-center"
                    style={{ width: `${(employeeStatusBars.compliant.value / totalEmployees) * 100}%` }}
                  >
                    {employeeStatusBars.compliant.value > 500 && <span className="text-[10px] text-white font-medium">{employeeStatusBars.compliant.value.toLocaleString()}</span>}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px]">
            {employeeByStatus.map(e => (
              <div key={e.name} className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: e.color }} />
                <span className="text-muted-foreground">{e.name === "Non-Compliant" ? "Non-C" : e.name === "Over-Compliant" ? "Over" : "Comp"}</span>
                <span className="font-medium">{e.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* By Category Chart - Horizontal Stacked Bars */}
      <Card className="p-3" data-testid="category-chart">
        <p className="text-xs font-medium mb-1">By Category</p>
        <div className="h-[120px]">
          {categoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={categoryData} layout="vertical" margin={CHART_MARGIN_COMPACT}>
                <XAxis type="number" scale="sqrt" domain={SQRT_DOMAIN} hide />
                <YAxis 
                  type="category" 
                  dataKey="name" 
                  width={90} 
                  tick={AXIS_TICK_SMALL}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      const nonCompliant = data["Non-Compliant"] ?? 0;
                      const overCompliant = data["Over-Compliant"] ?? 0;
                      const compliant = data["Compliant"] ?? 0;
                      
                      return (
                        <div className="bg-popover text-foreground border rounded-lg shadow-lg p-2.5 text-xs min-w-[140px]">
                          <div className="font-semibold border-b pb-1 mb-1.5">{data.fullName}</div>
                          <div className="space-y-0.5">
                            {nonCompliant > 0 && (
                              <div style={{color: '#EF4444'}}>Non-Compliant: {nonCompliant}</div>
                            )}
                            {overCompliant > 0 && (
                              <div style={{color: '#F59E0B'}}>Over-Compliant: {overCompliant}</div>
                            )}
                            {compliant > 0 && (
                              <div style={{color: '#10B981'}}>Compliant: {compliant}</div>
                            )}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="Non-Compliant" stackId="a" fill="#EF4444" />
                <Bar dataKey="Over-Compliant" stackId="a" fill="#F59E0B" />
                <Bar dataKey="Compliant" stackId="a" fill="#10B981" radius={BAR_RADIUS_RIGHT} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-xs text-muted-foreground">No data</div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default DashboardCharts;
